/**
 * Sidebar Component
 *
 * Side navigation menu for the DiscordMinstrel platform.
 * Displays navigation links based on user role and current route.
 */

import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, isGlobalAdmin, isTenantAdmin, type User } from '../../contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

interface SidebarProps {
  /** Whether the sidebar is currently open (mobile) */
  isOpen: boolean;
  /** Callback to close sidebar (mobile) */
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Required minimum role, or undefined for all authenticated users */
  requiredRole?: 'GLOBAL_ADMIN' | 'TENANT_ADMIN';
}

interface NavSection {
  title: string;
  items: NavItem[];
}

// =============================================================================
// Icons
// =============================================================================

const DashboardIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
    />
  </svg>
);

const BotIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const VoiceIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
    />
  </svg>
);

const TenantsIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const NodesIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
    />
  </svg>
);

const ReportsIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const LiteLLMIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const MonitoringIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const UsageIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
    />
  </svg>
);

const AuditIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
    />
  </svg>
);

const HeadscaleIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
    />
  </svg>
);

const TokenIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
    />
  </svg>
);

const GPUIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
  </svg>
);

const SearchIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

// =============================================================================
// Navigation Data
// =============================================================================

const navigationSections: NavSection[] = [
  {
    title: 'Main',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
      { label: 'Bots', path: '/bots', icon: <BotIcon /> },
      { label: 'Voice Profiles', path: '/voices', icon: <VoiceIcon /> },
    ],
  },
  {
    title: 'Monitoring',
    items: [
      {
        label: 'System Health',
        path: '/monitoring',
        icon: <MonitoringIcon />,
        requiredRole: 'TENANT_ADMIN',
      },
    ],
  },
  {
    title: 'Reports',
    items: [
      { label: 'Usage Reports', path: '/reports/usage', icon: <UsageIcon /> },
      { label: 'Cost Reports', path: '/reports/costs', icon: <ReportsIcon /> },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        label: 'Tenants',
        path: '/admin/tenants',
        icon: <TenantsIcon />,
        requiredRole: 'GLOBAL_ADMIN',
      },
      {
        label: 'Users',
        path: '/admin/users',
        icon: <UsersIcon />,
        requiredRole: 'TENANT_ADMIN',
      },
      {
        label: 'Remote Nodes',
        path: '/admin/nodes',
        icon: <NodesIcon />,
        requiredRole: 'TENANT_ADMIN',
      },
      {
        label: 'Headscale VPN',
        path: '/admin/headscale',
        icon: <HeadscaleIcon />,
        requiredRole: 'GLOBAL_ADMIN',
      },
      {
        label: 'LLM Endpoints',
        path: '/admin/litellm',
        icon: <LiteLLMIcon />,
        requiredRole: 'TENANT_ADMIN',
      },
      {
        label: 'Audit Log',
        path: '/admin/audit',
        icon: <AuditIcon />,
        requiredRole: 'TENANT_ADMIN',
      },
      {
        label: 'Registration Tokens',
        path: '/admin/registration-tokens',
        icon: <TokenIcon />,
        requiredRole: 'GLOBAL_ADMIN',
      },
      {
        label: 'GPU Management',
        path: '/admin/gpus',
        icon: <GPUIcon />,
        requiredRole: 'GLOBAL_ADMIN',
      },
      {
        label: 'SearXNG Search',
        path: '/admin/searxng',
        icon: <SearchIcon />,
        requiredRole: 'GLOBAL_ADMIN',
      },
    ],
  },
];

// =============================================================================
// Helpers
// =============================================================================

/**
 * Check if a navigation item should be visible based on user role
 */
function isItemVisible(item: NavItem, user: User | null): boolean {
  if (!item.requiredRole) {
    return true;
  }

  if (item.requiredRole === 'GLOBAL_ADMIN') {
    return isGlobalAdmin(user);
  }

  if (item.requiredRole === 'TENANT_ADMIN') {
    return isTenantAdmin(user);
  }

  return false;
}

/**
 * Filter section items based on user role
 */
function getVisibleItems(section: NavSection, user: User | null): NavItem[] {
  return section.items.filter((item) => isItemVisible(item, user));
}

// =============================================================================
// Sub-components
// =============================================================================

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  onClick?: () => void;
}

/**
 * Individual navigation link
 */
function NavLink({ item, isActive, onClick }: NavLinkProps) {
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`
        flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors
        ${
          isActive
            ? 'bg-discord-blurple text-white'
            : 'text-gray-300 hover:bg-discord-light hover:text-white'
        }
      `}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className="flex-shrink-0">{item.icon}</span>
      <span>{item.label}</span>
    </Link>
  );
}

// =============================================================================
// Component
// =============================================================================

/**
 * Main sidebar navigation component
 */
export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();

  /**
   * Check if a path is active
   */
  const isActive = useCallback(
    (path: string): boolean => {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    },
    [location.pathname]
  );

  const sidebarContent = (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto px-4 py-6">
      {navigationSections.map((section) => {
        const visibleItems = getVisibleItems(section, user);

        // Don't render empty sections
        if (visibleItems.length === 0) {
          return null;
        }

        return (
          <div key={section.title}>
            <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {section.title}
            </h3>
            <div className="flex flex-col gap-1">
              {visibleItems.map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  isActive={isActive(item.path)}
                  onClick={onClose}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div className="mt-auto border-t border-discord-light pt-4">
        <div className="px-3 text-xs text-gray-500">
          <p>DiscordMinstrel v0.1.0</p>
        </div>
      </div>
    </nav>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed bottom-0 left-0 top-16 z-20 w-64 border-r border-discord-light bg-discord-darker
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Main navigation"
      >
        {sidebarContent}
      </aside>
    </>
  );
}

export default Sidebar;
